<template>
  <div class="dashboard-ai">
    <Preloader v-if="preloader" />
    
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div>
          <h1 class="page-title">Overall Progress - AI Indicative Results</h1>
          <p v-if="latestOverallObservation" class="latest-observation">
            {{ latestOverallObservation.observation }} - {{ latestOverallObservation.date }}
          </p>
        </div>
        <!-- Limitations Button -->
          <div>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn 
          icon
          small
          color="grey darken-2" 
          class="limitations-btn"
          v-bind="attrs"
          v-on="on"
          @click="showLimitationsDialog = true"
        >
          <v-icon>mdi-information-outline</v-icon>
        </v-btn>
      </template>
      <span>Limitations</span>
    </v-tooltip>
      <v-btn 
        text 
        small 
        color="primary" 
        class="edit-project-btn"
        @click="goToProjectSettings"
      >
        <v-icon small left>mdi-pencil</v-icon>
        Edit Project Info
      </v-btn>
        </div>
      </div>
    </div>
    <!-- 4 Block Grid -->
    <v-row class="blocks-grid" no-gutters>
      <!-- Top Left: Progress Dials -->
      <v-col cols="12" md="6" class="block-container">
        <div class="block-card progress-dials-block">
          <h3 class="block-title">Progress as on {{ latestDate }}</h3>
          <div class="dials-container">
            <!-- Single Multi-Ring Chart for All Categories -->
            <div class="single-chart-wrapper">
              <div class="chart-legend-custom">
                <div 
                  v-for="(label, index) in allCategoriesLabels" 
                  :key="index"
                  class="legend-item-custom"
                >
                  <span 
                    class="legend-color" 
                    :style="{ backgroundColor: getCategoryColor(index) }"
                  ></span>
                  <span class="legend-text">{{ label.label }} ({{ label.value }}%)</span>
                </div>
              </div>
              <div class="dial-wrapper overall-dial">
                <div class="dial-actions">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="dial-edit-icon" @click="openEditDialog('overall')" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-pencil</v-icon>
                      </div>
                    </template>
                    <span>Sync your real % progress so the AI output stays accurate.</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="dial-manual-update-icon" @click="openManualUpdateDialog" v-bind="attrs" v-on="on">
                        <v-icon small>mdi-update</v-icon>
                      </div>
                    </template>
                    <span>Manual Update</span>
                  </v-tooltip>
                </div>
                <apexchart
                  type="radialBar"
                  height="350"
                  :options="allCategoriesDialOptions"
                  :series="allCategoriesProgress"
                />
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Top Right: Project Progress Curve -->
      <v-col cols="12" md="6" class="block-container">
        <div class="block-card chart-block">
          <h3 class="block-title">Project Progress Curve</h3>
          <div class="chart-container">
            <apexchart
              type="line"
              height="260"
              :options="lineChartOptions"
              :series="lineChartSeries"
              @dataPointSelection="onChartPointClick"
              @markerClick="onChartPointClick"
            />
            <div class="chart-legend">
              <div 
                v-for="(series, index) in lineChartSeries" 
                :key="index"
                class="legend-item"
                :class="{ disabled: !series.visible }"
                @click="toggleSeries(index)"
              >
                <span 
                  class="legend-color" 
                  :style="{ backgroundColor: series.color }"
                ></span>
                <span class="legend-label">{{ series.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Bottom Left: Timelapse Video -->
      <v-col cols="12" md="6" class="block-container">
        <div class="block-card timelapse-block">
          <div class="timelapse-container">
            <Pano 
              v-if="checkcam360 && timelapseUrl" 
              class="timelapse-video" 
              @on-load="onTimelapseLoad" 
              type="video" 
              :source="timelapseUrl"
            />
            <video
              v-else-if="timelapseUrl"
              class="timelapse-video"
              controls
              ref="timelapseVideo"
            >
              <source :src="timelapseUrl" type="video/mp4">
            </video>
            <v-speed-dial 
              v-model="shareFab" 
              direction="top" 
              transition="slide-y-transition"
              class="share-dial"
            >
              <template v-slot:activator>
                <v-btn fab dark small color="#ff5566">
                  <v-icon>mdi-share-variant</v-icon>
                </v-btn>
              </template>
              <v-tooltip left v-if="canShareWhatsApp">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn fab small color="amber" v-bind="attrs" v-on="on" @click="shareTimelapse('whatsapp')">
                    <v-icon>mdi-whatsapp</v-icon>
                  </v-btn>
                </template>
                <span>WhatsApp</span>
              </v-tooltip>
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn fab small color="red" v-bind="attrs" v-on="on" @click="shareTimelapse('email')">
                    <v-icon>mdi-email</v-icon>
                  </v-btn>
                </template>
                <span>Email</span>
              </v-tooltip>
            </v-speed-dial>
          </div>
        </div>
      </v-col>

      <!-- Bottom Right: Minimized Dashboard -->
      <v-col cols="12" md="6" class="block-container">
        <div class="block-card dashboard-mini-block">
          <div class="mini-dashboard-header">
          </div>
          <div class="mini-image-container">
            <!-- Image Menus -->
            <div class="image_menus" v-if="!showannotation">

            

              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mx-1 goto-btn menusioc" fab dark x-small color="#000000" title="Go to Dashboard" v-bind="attrs" v-on="on">
                    <v-icon dark color="#ffffff"  @click="goToDashboard">
                      mdi-arrow-top-right
                    </v-icon>
                  </v-btn>
                </template>
                <span>Go to Dashboard</span>
              </v-tooltip>
              
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mx-1 editHomeImage menusioc" fab dark x-small color="#000000" title="Image Markup" v-bind="attrs" v-on="on">
                    <v-icon dark color="#ffffff" @click="annotadeImage">
                      mdi-pencil
                    </v-icon>
                  </v-btn>
                </template>
                <span>'Image Markup</span>
              </v-tooltip>
              
              <v-speed-dial v-model="fab" direction="left" transition="slide-x-reverse-transition">
                <template v-slot:activator>
                  <v-tooltip left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-1 menusioc" fab dark x-small color="#000000" title="Share Image" v-bind="attrs" v-on="on">
                        <v-icon dark color="#ffffff" >
                          mdi-share-variant
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Share Image</span>
                  </v-tooltip>
                </template>

                <v-tooltip left v-if="canShareWhatsApp">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn fab small color="amber" v-bind="attrs" v-on="on" @click="boxopenshareImageByEmails('whatsapp')">
                      <v-icon>mdi-whatsapp</v-icon>
                    </v-btn>
                  </template>
                  <span>WhatsApp</span>
                </v-tooltip>

                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn fab small color="amber" v-bind="attrs" v-on="on" @click="boxopenshareImageByEmails('email')">
                      <v-icon>mdi-email-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Email</span>
                </v-tooltip>
              </v-speed-dial>
              
              <v-tooltip v-if="!openAccessFeature" left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mx-1 menusioc" fab dark x-small color="#000000" title="Add Event Timeline" v-bind="attrs" v-on="on">
                    <v-icon dark color="#ffffff" @click="evnettaginboxopener">
                      mdi-tag-outline
                    </v-icon>
                  </v-btn>
                </template>
                <span>Add Event Timeline</span>
              </v-tooltip>
              
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mx-1 menusioc" fab dark x-small color="#000000" title="Favorite" v-bind="attrs" v-on="on">
                    <v-icon dark color="#ffffff" @click="savefavoourite">
                      {{ favourite }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ isFav ? 'Remove Favorite' : 'Add To Favorite' }}</span>
              </v-tooltip>
              
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mx-1 menusioc" fab dark x-small color="#000000" title="Weather" v-bind="attrs" v-on="on">
                    <v-icon dark color="#ffffff" @click="wathershow">
                      mdi-weather-partly-snowy-rainy
                    </v-icon>
                  </v-btn>
                </template>
                <span>Weather</span>
              </v-tooltip>
              
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn class="mx-1 menusioc" fab dark x-small color="#000000" title="Download Image" v-bind="attrs" v-on="on">
                    <v-icon dark color="#ffffff" @click="downloadImage">
                      mdi-download-circle-outline
                    </v-icon>
                  </v-btn>
                </template>
                <span>Download Image</span>
              </v-tooltip>
          
            </div>

            <img 
              :src="currentImage" 
              alt="Latest camera image"
              class="mini-image"
              @click="openFullscreenImage"
            />
            <!-- <div class="image-navigation">
              <v-btn 
                icon 
                small 
                @click="previousImage"
                :disabled="!canGoPrevious"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn 
                icon 
                small 
                @click="nextImage"
                :disabled="!canGoNext"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div> -->
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Observations Table -->
    <div class="observations-table-container" v-if="categoryObservations.length > 0">
      <div class="block-card observations-card">
        <h3 class="block-title">Latest Observations</h3>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Category</th>
                <th class="text-center">Completion %</th>
                <th class="text-left">Latest Observation As on {{ latestDate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in categoryObservations" :key="index">
                <td class="category-name">{{ item.category }}</td>
                <td class="text-center completion-percent">{{ item.completion }}%</td>
                <td class="observation-text">{{ item.observation || 'No observation available' }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>

    <!-- Execution Velocity Table -->
    <div class="execution-velocity-container" v-if="executionVelocityData.length > 0">
      <div class="block-card velocity-card">
        <h3 class="block-title">Execution Velocity</h3>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Month</th>
                <th class="text-center">Excavation Δ%</th>
                <th class="text-center">Structural Δ%</th>
                <th class="text-center">Architectural Δ%</th>
                <th class="text-center">Facade Δ%</th>
                <th class="text-center">Painting Δ%</th>
                <th class="text-center">Overall Δ%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in executionVelocityData" :key="index">
                <td class="month-name">{{ row.month }}</td>
                <td class="text-center delta-value">{{ formatDelta(row.excavation) }}</td>
                <td class="text-center delta-value">{{ formatDelta(row.structural) }}</td>
                <td class="text-center delta-value">{{ formatDelta(row.architectural) }}</td>
                <td class="text-center delta-value">{{ formatDelta(row.facade) }}</td>
                <td class="text-center delta-value">{{ formatDelta(row.painting) }}</td>
                <td class="text-center delta-value">{{ formatDelta(row.overall) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>

    <!-- Bottom Menu: Refresh Image & Timeline -->
    <div class="bottom-menu">
      <div class="timeline" :class="{ active: showtimeline }">
        <div class="timeline-controls">
          <ul class="listitem">
            <li v-if="!regulertimeline && ov_access">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon dark v-bind="attrs" v-on="on" @click="delete_img_archive">
                    mdi-delete
                  </v-icon>
                </template>
                <span>Delete Image</span>
              </v-tooltip>
            </li>
            <li v-if="!regulertimeline">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon dark v-bind="attrs" v-on="on" @click="regulartimelineshow">
                    mdi-view-carousel-outline
                  </v-icon>
                </template>
                <span>Full Timeline</span>
              </v-tooltip>
            </li>
            <li v-if="!regulertimeline">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon dark v-bind="attrs" v-on="on" @click="reloade">
                    mdi-autorenew
                  </v-icon>
                </template>
                <span>Refresh</span>
              </v-tooltip>
            </li>
            <li v-if="regulertimeline" style="position: relative; right: -83px;">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon dark v-bind="attrs" v-on="on" @click="reloade">
                    mdi-autorenew
                  </v-icon>
                </template>
                <span>Refresh</span>
              </v-tooltip>
            </li>
          </ul>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <a 
                class="timelinetoggle" 
                v-bind="attrs" 
                v-on="on" 
                :class="{ active: showtimeline }"
                @click="showtimeline = !showtimeline"
              >
                <v-icon color="#000">mdi-arrow-up-drop-circle</v-icon>
              </a>
            </template>
            <span>Full Timeline</span>
          </v-tooltip>
        </div>

        <carousel 
          :key="refresh" 
          class="timelinec"
          :responsive="{ 0: { items: 2, nav: false }, 600: { items: 3, nav: true }, 1000: { items: 8, nav: true, slideBy: 7 } }"
          :autoplay="false" 
          :dots="false" 
          :margin="8" 
          :navText="['', '']"
          v-if="daywiseimageidsarray.length > 0 && regulertimeline"
        >
          <div 
            v-for="(image, index) in daywiseimageidsarray" 
            :key="index"
            @click="selectThumainal(image.prefix, image.image_name)"
          >
            <div class="fixImageBox">
              <img :src="image.url" />
            </div>
            <p class="disply_date">{{ image.display_date }}</p>
          </div>
        </carousel>
        <carousel 
          :key="refreshtwoo" 
          class="timelinec"
          :responsive="{ 0: { items: 2, nav: false }, 600: { items: 3, nav: true }, 1000: { items: 8, nav: true, slideBy: 7 } }"
          :autoplay="false" 
          :dots="false" 
          :margin="8" 
          :navText="['', '']"
          v-if="timeslots.length > 0 && !regulertimeline"
        >
          <div v-for="(image, index) in timeslots" :key="index">
            <input 
              type="checkbox" 
              v-model="selected_archive" 
              class="checkboxxsle" 
              :value="image"
              v-if="ov_access" 
              return-object 
              @click="OnSelect()" 
            />
            <div class="fixImageBox">
              <img :src="image.thumb_url" @click="getArchiveImageId(image.image_id)" />
            </div>
            <p class="disply_date">{{ image.updated_time }}</p>
          </div>
        </carousel>
      </div>
    </div>

 

    <!-- Manual Update Dialog -->
    <v-dialog v-model="manualUpdateDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>Manual Update Progress</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="manualUpdateInstructions"
            label="Enter your instructions"
            placeholder="Ex. update the overall progress to 30% instead of 35%"
            outlined
            rows="4"
            hint="Provide clear instructions on what progress values need to be updated"
            persistent-hint
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeManualUpdateDialog">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="submitManualUpdate"
            :loading="manualUpdateLoading"
            :disabled="!manualUpdateInstructions || manualUpdateInstructions.trim() === ''"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Overall Progress Dialog -->
    <v-dialog v-model="editDialog" max-width="400">
      <v-card>
        <v-card-title>Edit Overall Progress</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="editProgressValue"
            type="number"
            label="Completion Percentage"
            min="0"
            max="100"
            outlined
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveProgress">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Limitations Dialog -->
    <v-dialog v-model="showLimitationsDialog" max-width="600">
      <v-card>
        <v-card-title>Limitations</v-card-title>
        <v-card-text>
          <ul class="limitations-list">
            <li>User may need to sometimes update progress manually to align the AI progress with the original progress.</li>
            <li>If the camera location changes, results may change. Client need to inform OpticVyu team about such changes.</li>
            <li>For more increased accuracy, OpticVyu may provide manual checks along with AI reporting at an extra cost.</li>
            <li>Only enable one camera for AI processing for a particular site area. Multiple cameras looking at the same area might duplicate the data & create ambiguous results.</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showLimitationsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Date Picker Dialog -->
    <v-dialog v-model="dialog" persistent scrollable max-width="600px">
      <v-card>
        <v-date-picker 
          :color="activecolor" 
          @change="getSelectedDate($event)" 
          v-model="picker"
          :max="maxdate" 
          :allowed-dates="dateActiver ? allowedDates : alldates"
          :picker-date.sync="pickerDate"
        ></v-date-picker>
        <div class="timeList">
          <v-list mandatory rounded max-height="341px">
            <v-list-item-group>
              <v-dialog v-model="timeslotloader" persistent width="300">
                <v-card :color="activecolor" dark>
                  <v-card-text>
                    Please stand by
                    <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
                  </v-card-text>
                </v-card>
              </v-dialog>
              <v-list-item 
                v-for="(item, i) in timeslots" 
                :key="i"
                @change="selectTimeSlot(item.image_id)" 
                class="itemBottomMargin"
              >
                <v-list-item-content active-class="amber accent-3-darken-3--text text--accent-4">
                  <v-chip>{{ item.updated_time }}</v-chip>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </div>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="green darken-1" text @click="dialogClose">
            Close
          </v-btn>
          <v-btn color="green darken-1" text @click="dialogClose">
            Done
          </v-btn>
          <v-snackbar v-model="snackbarDatesWithImages" :multi-line="multiLine" top right timeout="3000" type="success">
            {{ archiveCalenderyearMonth }}
            <template v-slot:action="{ attrs }">
              <v-btn color="red" text v-bind="attrs" @click="snackbarDatesWithImages = false">
                Close
              </v-btn>
            </template>
          </v-snackbar>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2 dateWithImageButton" color="amber" @click="datesWithImage()" dark v-bind="attrs" v-on="on">
                <v-icon dark>
                  mdi-checkbox-marked-circle
                </v-icon>
              </v-btn>
            </template>
            <span>Capturing Insights</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Fullscreen Image Dialog -->
    <v-dialog 
      v-model="fullscreenImageDialog" 
      fullscreen 
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="fullscreen-image-card">
        <v-card-actions class="fullscreen-header">
          <v-spacer></v-spacer>
          <v-btn icon @click="fullscreenImageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
        <div class="fullscreen-image-container">
          <img :src="fullscreenImageUrl" alt="Fullscreen image" class="fullscreen-image" />
          <!-- Right side options similar to dashboard -->
          <div class="fullscreen-options">
            <!-- Add dashboard right side options here -->
          </div>
          <!-- Camera tag at bottom left -->
          <div class="camera-tag">
            {{ currentCameraTag }}
          </div>
        </div>
      </v-card>
    </v-dialog>



    <!-- Tooltip for chart hover -->
    <v-tooltip 
      v-model="chartTooltip.show"
      :position-x="chartTooltip.x"
      :position-y="chartTooltip.y"
      absolute
    >
      <span>{{ chartTooltip.text }}</span>
    </v-tooltip>

    <v-dialog transition="dialog-bottom-transition" max-width="600" color="amber accent-3" v-model="shareboxImage">
            <template>
                <v-card>
                    <v-toolbar color="amber accent-3" dark> Share Image By Email  </v-toolbar>

                    <!-- <v-btn color="#1f1f1e" class="ma-2 white--text procorebutton" @click="shareToProcore"
                        v-if="shareImageByEmail">
                        Share Image By Email
                    </v-btn> -->

                    <v-form ref="form2" v-model="valid2" lazy-validation @submit.prevent="submit2">
                        <v-container>
                            <v-row>
                                <v-col cols="12" sm="12">
                                    <!-- <v-text-field v-model="email" :label="lang.Email" required
                                        :rules="emailrules"></v-text-field> -->
                                    <v-combobox v-model="email" :rules="shareEmailRules" required chips solo clearable
                                        label="Email" :hint="emailFieldHint" persistent-hint multiple deletable-chips>
                                        <template v-slot:append>
                                            <span v-if="isEmailDomainRestricted" class="email-domain-suffix">{{ emailDomainSuffix }}</span>
                                        </template>
                                    </v-combobox>
                                </v-col>
                                <v-col cols="12" sm="12">
                                    <v-text-field v-model="subject" label="Subject" required></v-text-field>
                                </v-col>

                                <v-col cols="12">
                                    <v-btn class="mr-4" type="submit" :disabled="!valid2" color="mylightbutton">
                                        Send
                                        <v-progress-circular v-if="send" indeterminate
                                            color="green"></v-progress-circular>
                                    </v-btn>
                                    <v-btn  @click="shareboxImage = false" color="mydarkbutton">
                                        Cancel
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card>
            </template>
        </v-dialog>

    <v-dialog
      transition="dialog-bottom-transition"
      max-width="600"
      color="amber accent-3"
      v-model="sharebox"
    >
      <template>
        <v-card>
          <v-toolbar color="amber accent-3" dark>
             Share By Email
            <span
              class="btn btn-info text-white copy-btn ml-auto"
              @click.stop.prevent="copyTestingCode"
            >
              <v-btn depressed color="primary"> Copy URL </v-btn>
              <input type="hidden" id="testing-code" :value="timelapseUrl" />
            </span>
          </v-toolbar>
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="submit"
          >
            <v-container>
              <v-row>
                <v-col cols="12" sm="12">
                
                  <v-combobox
                    v-model="email"
                    :rules="shareEmailRules"
                    required
                    chips
                    solo
                    clearable
                    label="Email"
                    :hint="emailFieldHint"
                    persistent-hint
                    multiple
                    deletable-chips
                >
                    <template v-slot:append>
                        <span v-if="isEmailDomainRestricted" class="email-domain-suffix">{{ emailDomainSuffix }}</span>
                    </template>
                </v-combobox>
                </v-col>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="subject"
                    label="Subject"
                    required
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-btn
                    class="mr-4"
                    type="submit"
                    :disabled="!valid"
                    color="mylightbutton"
                  >
                    Send
                    <v-progress-circular
                      v-if="send"
                      indeterminate
                      color="green"
                    ></v-progress-circular>
                  </v-btn>
                  <v-btn @click="sharebox = false" color="mydarkbutton">
                    Cancel
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card>
      </template>
    </v-dialog>
    <v-dialog transition="dialog-bottom-transition" max-width="600" color="amber accent-3" v-model="whatsapp_sharebox_image" style="z-index: 99999;">
                <v-card class="whatsappTimelapseDialog">
                    <v-toolbar color="amber accent-3" dark> Share By Whatsapp </v-toolbar>
                        <v-container>
                            <v-row>
                                <v-col cols="12" sm="12">
                                    <vue-phone-number-input v-model="whatsappNumber" label="Phone Number" :rules="whatsappRules"
                    required placeholder="Enter your phone number"
                    :default-country-code="'IN'" @update="onPhoneNumberUpdate"></vue-phone-number-input>
                                </v-col>
                                <v-col cols="12">
                                    <v-btn class="mr-4" @click="shareWhatsappImage" :disabled="!phoneData.isValid" color="mylightbutton">
                                       Send
                                        <v-progress-circular v-if="send" indeterminate
                                            color="green"></v-progress-circular>
                                    </v-btn>
                                    <v-btn @click="whatsapp_sharebox_image = false" color="mydarkbutton">
                                      Cancel
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>
                </v-card>
        </v-dialog>
    <v-dialog transition="dialog-bottom-transition" max-width="600" color="amber accent-3" v-model="whatsapp_sharebox" style="z-index: 99999;">
                <v-card class="whatsappTimelapseDialog">
                    <v-toolbar color="amber accent-3" dark> Share By Whatsapp </v-toolbar>
                        <v-container>
                            <v-row>
                                <v-col cols="12" sm="12">
                                    <vue-phone-number-input v-model="whatsappNumber" label="Phone Number" :rules="whatsappRules"
                    required placeholder="Enter your phone number"
                    :default-country-code="'IN'" @update="onPhoneNumberUpdate"></vue-phone-number-input>
                                </v-col>
                                <v-col cols="12">
                                    <v-btn class="mr-4" @click="shareWhatsapp" :disabled="!phoneData.isValid" color="mylightbutton">
                                       Send
                                        <v-progress-circular v-if="send" indeterminate
                                            color="green"></v-progress-circular>
                                    </v-btn>
                                    <v-btn @click="whatsapp_sharebox = false" color="mydarkbutton">
                                      Cancel
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>
                </v-card>
        </v-dialog>
        <v-dialog transition="dialog-bottom-transition" max-width="600" color="amber accent-3"
            v-model="sharebox_annotate">
            <template>
                <v-card>
                    <v-toolbar color="amber accent-3" dark> ShareByEmail </v-toolbar>



                    <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="submit_annotate">
                        <v-container>
                            <v-row>
                                <v-col cols="12" sm="12">
                                    <v-text-field v-model="email_annotate" label="Email" required
                                        :rules="shareEmailRulesString">
                                        <template v-slot:append>
                                            <span v-if="isEmailDomainRestricted" class="email-domain-suffix">{{ emailDomainSuffix }}</span>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12">
                                    <v-text-field v-model="subject_annotate" label="Subject"
                                        required></v-text-field>
                                </v-col>

                                <v-col cols="12">
                                    <v-btn class="mr-4" type="submit" :disabled="!valid" color="mylightbutton">
                                        Send
                                        <v-progress-circular v-if="send" indeterminate
                                            color="green"></v-progress-circular>
                                    </v-btn>
                                    <v-btn @click="sharebox_annotate = false" color="mydarkbutton">
                                        Cancel
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card>
            </template>
        </v-dialog>
        <v-dialog transition="dialog-bottom-transition" max-width="400" color="amber accent-3" v-model="watherview">
            <div class="weatherContainer text-center" style="z-index: 2">
                <button data-dismiss="modal" id="popupCloseButton" class="close" title="close"
                    style="font-size: 23px; opacity: 0.5" @click="watherview = false">
                    ×
                </button>
                <div class="weatherWrapper">
                    <h4 class="weatherTitle"> Weather Info</h4>
                    <div class="text-center" v-html="watherdata" id="weatherResult"></div>
                    <small> Data is accessed through OpenWeatherMap service. In order to get exact site data, update the
                        site latitude - longitude. <br>
                        <span class="hetitlec active" @click="setrouter('/dashboard/settings/profile')"> Update Site
                            Location </span>
                    </small>
                </div>
            </div>
        </v-dialog>
         <!-- event taggin popub box -->

         <v-dialog transition="dialog-bottom-transition" max-width="600" color="amber accent-3" v-model="evnettaginbox">
            <template>
                <v-card>
                    <v-toolbar color="amber accent-3" dark>
                        Add Event Timeline
                    </v-toolbar>
                    <v-form ref="form" lazy-validation @submit.prevent="submitevent">
                        <v-container>
                            <v-row>
                                <v-col cols="12" sm="12">
                                    <v-textarea counter v-model="eventdata" label="Discribe The Event" required
                                        :rules="rules"></v-textarea>
                                </v-col>

                                <v-col cols="12">
                                    <v-btn class="mr-4" type="submit" :disabled="!valid" color="mylightbutton">
                                        Send
                                        <v-progress-circular v-if="send" indeterminate
                                            color="green"></v-progress-circular>
                                    </v-btn>
                                    <v-btn @click="evnettaginbox = false" color="mydarkbutton">
                                        Cancel
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card>
            </template>
        </v-dialog>
        <div class="imageeditor active" v-if="showannotation">
            <v-btn icon dark @click="showannotationclose" class="oveicon clsoe">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-btn dark text @click="SaveandSend()" class="oveicon save">
               Save and Share
            </v-btn>
            <!-- <v-btn
                dark
                text
               @click="onlySend()"
                class="oveicon sendd"
              >
                Send
              </v-btn> -->
            <tui-image-editor ref="tuiImageEditor" :include-ui="useDefaultUI" :options="options" :key="tui_re_render"
                v-if="showannotation"></tui-image-editor>
        </div>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import axios from "axios";
import Preloader from "../../components/PreLoader";
import { Pano } from "vuejs-vr";
import carousel from "vue-owl-carousel";
import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import {
    ImageEditor
} from "@toast-ui/vue-image-editor";
import "tui-image-editor/dist/tui-image-editor.css";
import image1 from "../../assets/FKUg3r.jpg";
import emailDomainMixin from "../../utils/emailDomainMixin";
export default {
  mixins: [emailDomainMixin],
  name: "DashboardAI",
  components: {
    apexchart: VueApexCharts,
    Preloader,
    Pano,
    carousel,
    VuePhoneNumberInput,
    "tui-image-editor": ImageEditor,
  },
  data() {
    return {
      preloader: false,
      latestDate: "",
      overallProgress: 0,
      multiRingProgress: [0, 0, 0], // Three rings: [outermost, middle, innermost]
      categoryDials: [],
      categoryMultiRings: {}, // Store multi-ring data for each category
      allCategoriesProgress: [], // All categories as rings in single chart
      allCategoriesLabels: [], // Labels for each category ring
      lineChartSeries: [],
      lineChartOptions: {},
      sortedDates: [],
      timelapseUrl: "",
      checkcam360: false,
      currentImage: "",
      currentImageIndex: 0,
      imageList: [],
      shareFab: false,
      editDialog: false,
      editProgressValue: 0,
      editCategory: "",
      showLimitationsDialog: false,
      manualUpdateDialog: false,
      manualUpdateInstructions: "",
      manualUpdateLoading: false,
      fullscreenImageDialog: false,
      fullscreenImageUrl: "",
      currentCameraTag: "",
      chartTooltip: {
        show: false,
        x: 0,
        y: 0,
        text: ""
      },
      projectId: "",
      cameraId: "",
      cameraS3Dir: "",
      latestReportId: "",
      apiData: null,
      showtimeline: false,
      regulertimeline: true,
      refresh: 0,
      refreshtwoo: 0,
      daywiseimageidsarray: [],
      timeslots: [],
      ov_access: false,
      selected_archive: [],
      show_deletebtn: false,
      projectid: "",
      current_cam:{},
      sharebox: false,
      whatsapp_sharebox: false,
      whatsappNumber: '',
            whatsappRules: [
      v => !!v || 'Phone number is required',
      v => /^\+?[1-9]\d{1,14}$/.test(v) || 'Enter a valid phone number with country code'
    ],
    phoneData:{},
    // Image menu features
    showannotation: false,
    activeanotate: false,
    fab: false,
    favourite: 'mdi-heart-outline',
    isFav: false,
    openAccessFeature: false,
    dialog: false,
    picker: null,
    maxdate: new Date().toISOString().substr(0, 10),
    dateActiver: false,
    allowedDates: null,
    alldates: null,
    pickerDate: null,
    timeslotloader: false,
    snackbarDatesWithImages: false,
    archiveCalenderyearMonth: '',
    activecolor: 'amber accent-3',
    userdata: {},
    lang: {},
    evnettaginbox: false,
    eventdata: '',
    options: {
                cssMaxWidth: 700,
                cssMaxHeight: 500,
                includeUI: {
                    loadImage: {
                        path: image1,
                        name: "SampleImage",
                    },
                    initMenu: "draw",
                    menuBarPosition: "left",

                },
                ErrorMsg: 'Sorry Image is not load',
            },
            useDefaultUI: true,
            annotateurl:'',
            annotate_data:'',
            sharebox_annotate:false,
            email_annotate:'',
            subject_annotate:'',
            sharebyname:'',
            filename: "annotage_image.jpg",
            annotate_newbase64 : '',
            imgname:'',
            tui_re_render: 0,
            project : '',
            emailrules_req: [
                v => /^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)?$/.test(v) || 'E-mail must be valid',

            ],
            emailrules: [
                v => /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4},?)+$/.test(v) || 'E-mail must be valid',

            ],
            subject_req: [v => !!v || "Subject is required"],
            message_req: [v => !!v || "Message is required"],
            watherdata: "",
            valid :true,
            send :false,
            watherview : false,
            subject:'',
            email :'',
            message :'',
            allEmails :'',
            timezone : '00',
          
            dronev :false,
            currentZoneDate :'',
            multiLine: false,
            snackbar: false,
            valid2 :true,
            whatsapp_sharebox_image: false,
            phoneData_image:{}, 
            shareboxImage : false,
           

    };
  },
  computed: {
    shareEmailRules() {
      return this.emailRulesFor(this.emailrules, true);
    },
    shareEmailRulesString() {
      return this.emailRulesFor(this.emailrules, false);
    },
    params() {
            return {
                to: this.normalizeProjectEmails(this.email).join(','),
                message: "",
                subject: this.subject,
                attachment: this.currentImage,
                project: this.project,
                org: this.org,
                city: this.city,
            };
        },
        paramstimelapse() {
      return {
        to: this.normalizeProjectEmails(this.email).join(','),
        message: "",
        subject: this.subject,
        attachment: this.timelapseUrl,
        project: this.project,
        org: this.org,
        city: this.city,
      };
    },
        whatsapp_params_image() {
            return {
                to: this.phoneData.formattedNumber,
                attachment: this.currentImage,
                project: this.project,
                org: this.org,
                city: this.city,
            };
        },
        params_annotate() {
            return {
                to: this.normalizeProjectEmails(this.email_annotate),
                message: "",
                subject: this.subject_annotate,
                attachment: this.annotateurl,
                project: this.project,
                org: this.org,
                city: this.city,
                shareby: this.sharebyname,
            };
        },
    canGoPrevious() {
      return this.currentImageIndex > 0;
    },
    canGoNext() {
      return this.currentImageIndex < this.imageList.length - 1;
    },
    overallDialOptions() {
      return {
        chart: {
          toolbar: { show: false },
          type: "radialBar"
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: "50%"
            },
            track: {
              background: "#e2e8f0",
              strokeWidth: "100%",
              margin: 5
            },
            dataLabels: {
              name: {
                show: false
              },
              value: {
                fontSize: "28px",
                fontWeight: 700,
                color: "#0f172a",
                formatter: () => `${Math.round(this.overallProgress)}%`,
                offsetY: 0,
                show: true
              },
              total: {
                show: false
              }
            }
          }
        },
        colors: ["#ff1493", "#1e3a8a", "#00d9ff"], // Hot pink (outermost), Deep blue (middle), Light blue (innermost)
        stroke: {
          lineCap: "round"
        }
      };
    },
    smallDialOptions() {
      return {
        chart: {
          toolbar: { show: false }
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: "50%"
            },
            track: {
              background: "#e2e8f0"
            },
            dataLabels: {
              name: {
                show: false
              },
              value: {
                fontSize: "16px",
                fontWeight: 600,
                color: "#0f172a",
                formatter: val => `${Math.round(val)}%`
              }
            }
          }
        },
        colors: ["#3b82f6"]
      };
    },
    latestOverallObservation() {
      if (!this.apiData || !this.apiData.categories || !this.apiData.categories.overall) {
        return null;
      }

      const overall = this.apiData.categories.overall;
      if (overall.progress_history && overall.progress_history.length > 0) {
        const latestEntry = overall.progress_history[overall.progress_history.length - 1];
        return {
          observation: latestEntry.precise_observation || "",
          date: latestEntry.date || this.latestDate
        };
      }

      return null;
    },
    categoryObservations() {
      if (!this.apiData || !this.apiData.categories) {
        return [];
      }

      const categories = this.apiData.categories;
      const categoryMap = {
        structural_work: "Structural Work",
        external_facade: "External Façade",
        architectural_works: "Architectural Works",
        excavation: "Excavation",
        painting: "Painting",
        overall: "Overall"
      };

      // Define order: Categories first, Overall last
      const categoryOrder = ['structural_work', 'external_facade', 'architectural_works', 'excavation', 'painting', 'overall'];

      const observations = [];

      categoryOrder.forEach(key => {
        if (categories[key]) {
          const categoryData = categories[key];
          const completion = Math.round(categoryData.current_completion || 0);
          
          // Get latest observation from progress_history
          let observation = "";
          if (categoryData.progress_history && categoryData.progress_history.length > 0) {
            // Get the latest entry (last in array)
            const latestEntry = categoryData.progress_history[categoryData.progress_history.length - 1];
            observation = latestEntry.precise_observation || "";
          }

          observations.push({
            category: categoryMap[key],
            completion: completion,
            observation: observation
          });
        }
      });

      return observations;
    },
    executionVelocityData() {
      if (!this.apiData || !this.apiData.categories) {
        return [];
      }

      const categories = this.apiData.categories;
      const categoryKeys = {
        structural: 'structural_work',
        facade: 'external_facade',
        architectural: 'architectural_works',
        excavation: 'excavation',
        painting: 'painting',
        overall: 'overall'
      };

      // Collect all dates from all categories
      const allDates = new Set();
      Object.keys(categoryKeys).forEach(key => {
        const categoryKey = categoryKeys[key];
        if (categories[categoryKey] && categories[categoryKey].progress_history) {
          categories[categoryKey].progress_history.forEach(item => {
            allDates.add(item.date);
          });
        }
      });

      // Sort dates
      const sortedDates = Array.from(allDates).sort();

      // Group dates by month
      const monthMap = new Map(); // monthKey -> array of dates
      sortedDates.forEach((date) => {
        const dateObj = new Date(date);
        const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthMap.has(monthKey)) {
          monthMap.set(monthKey, []);
        }
        monthMap.get(monthKey).push(date);
      });

      // Get sorted month keys
      const sortedMonthKeys = Array.from(monthMap.keys()).sort();

      // Create rows for each date entry, grouped by month
      const monthlyData = [];
      let previousMonthLastDate = null;

      sortedMonthKeys.forEach((monthKey) => {
        const monthDates = monthMap.get(monthKey).sort();
        const firstDate = monthDates[0];
        const monthName = this.formatMonth(firstDate);

        // Create row for each date in the month
        monthDates.forEach((date, dateIndex) => {
          let monthLabel = monthName;
          
          // Label multiple entries in same month
          if (monthDates.length > 1) {
            if (dateIndex === 0) {
              monthLabel = monthName; // First entry
            } else if (dateIndex === 1 && monthDates.length === 2) {
              monthLabel = `${monthName} (mid)`;
            } else if (dateIndex === 1 && monthDates.length > 2) {
              monthLabel = `${monthName} (mid)`;
            } else if (dateIndex === 2) {
              monthLabel = `${monthName} (late)`;
            } else {
              monthLabel = `${monthName} (${dateIndex + 1})`;
            }
          }

          // For delta calculation: use last date of previous month
          const deltaPreviousDate = dateIndex === 0 
            ? previousMonthLastDate 
            : monthDates[dateIndex - 1];

          // Calculate deltas for each category
          const row = {
            month: monthLabel,
            date: date,
            structural: this.calculateDelta(categories[categoryKeys.structural], date, deltaPreviousDate),
            facade: this.calculateDelta(categories[categoryKeys.facade], date, deltaPreviousDate),
            architectural: this.calculateDelta(categories[categoryKeys.architectural], date, deltaPreviousDate),
            excavation: this.calculateDelta(categories[categoryKeys.excavation], date, deltaPreviousDate),
            painting: this.calculateDelta(categories[categoryKeys.painting], date, deltaPreviousDate),
            overall: this.calculateDelta(categories[categoryKeys.overall], date, deltaPreviousDate)
          };

          monthlyData.push(row);
        });

        // Update previous month's last date for next iteration
        previousMonthLastDate = monthDates[monthDates.length - 1];
      });

      return monthlyData;
    },
    allCategoriesDialOptions() {
      // Define colors for each category ring
      const colors = [
        "#008ffb", // Blue
        "#00e396", // Green
        "#feb019", // Orange
        "#ff4560", // Red
        "#775dd0", // Purple
        "#00d9ff", // Cyan
        "#ff1493", // Hot Pink
        "#1e3a8a"  // Deep Blue
      ];
      
      return {
        chart: {
          toolbar: { show: false },
          type: "radialBar"
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: "40%"
            },
            track: {
              background: "#e2e8f0",
              strokeWidth: "100%",
              margin: 5
            },
            dataLabels: {
              name: {
                show: false
              },
              value: {
                fontSize: "20px",
                fontWeight: 700,
                color: "#0f172a",
                formatter: () => `${Math.round(this.overallProgress)}%`,
                offsetY: 0,
                show: true
              },
              total: {
                show: true,
                label: "Overall",
                fontSize: "14px",
                fontWeight: 600,
                color: "#64748b",
                formatter: () => `${Math.round(this.overallProgress)}%`
              }
            }
          }
        },
        colors: colors.slice(0, this.allCategoriesProgress.length),
        stroke: {
          lineCap: "round"
        },
        legend: {
          show: false
        }
      };
    }
  },
  mounted() {
    this.initializeData();
    this.userdata = JSON.parse(localStorage.getItem("userdata"));
  },
  methods: {
    async getCameraId() {
      // Priority 1: Try to get camera_id from route params
      if (this.$route && this.$route.params && this.$route.params.camera_id) {
        const routeCameraId = this.$route.params.camera_id;
        try {
          const response = await axios.get(`/camera/${routeCameraId}`);
          if (response.data && response.data.success) {
            const camera = response.data.success;
            this.current_cam = camera;
            this.$store.dispatch("setCamera", camera);
            localStorage.setItem("cid", routeCameraId);
            return routeCameraId;
          }
        } catch (error) {
          console.warn("Error fetching camera from route params:", error);
        }
      }

      // Priority 2: Try to get camera_id from store
      if (this.$store.state.currentCamera && this.$store.state.currentCamera.camera_id) {
        this.current_cam = this.$store.state.currentCamera;
        return this.$store.state.currentCamera.camera_id;
      }

      // Priority 3: Try localStorage
      const cid = localStorage.getItem("cid");
      if (cid) {
        try {
          // Fetch camera info to set current_cam
          const response = await axios.get(`/camera/${cid}`);
          if (response.data && response.data.success) {
            const camera = response.data.success;
            this.current_cam = camera;
            // Update store
            this.$store.dispatch("setCamera", camera);
            return cid;
          }
        } catch (error) {
          console.warn("Error fetching camera from localStorage cid:", error);
        }
      }

      // Fallback: get from project cameras
      const currentProject = JSON.parse(localStorage.getItem("currentProject") || "{}");
      if (currentProject && currentProject.id) {
        try {
          const response = await axios.get(`/camera/${currentProject.id}`);
          if (response.data && response.data.success) {
            const cameras = Array.isArray(response.data.success) 
              ? response.data.success 
              : [response.data.success];
            
            // Filter active cameras
            const activeCameras = cameras.filter(cam => cam && cam.camera_active === 1);
            if (activeCameras.length > 0) {
              // Try to use camera from localStorage index first
              const currentCamIndex = localStorage.getItem("currentCamIndex");
              let camera;
              
              if (currentCamIndex !== null && activeCameras[currentCamIndex]) {
                camera = activeCameras[currentCamIndex];
              } else {
                camera = activeCameras[0];
              }
              
              this.current_cam = camera;
              // Update store and localStorage
              this.$store.dispatch("setCamera", camera);
              localStorage.setItem("cid", camera.camera_id);
              return camera.camera_id;
            }
          }
        } catch (error) {
          console.error("Error fetching cameras for project:", error);
        }
      }

      // Last resort: return null or default
      console.warn("Could not determine camera_id");
      return null;
    },
    calculateDelta(categoryData, currentDate, previousDate) {
      if (!categoryData || !categoryData.progress_history) {
        return null;
      }

      // Find current date entry
      const currentEntry = categoryData.progress_history.find(h => h.date === currentDate);
      if (!currentEntry) {
        return null;
      }

      const currentValue = currentEntry.completion_percentage || 0;

      // If no previous date, return null (first entry)
      if (!previousDate) {
        return null;
      }

      // Find previous date entry
      const previousEntry = categoryData.progress_history.find(h => h.date === previousDate);
      if (!previousEntry) {
        return null;
      }

      const previousValue = previousEntry.completion_percentage || 0;
      const delta = Math.round(currentValue - previousValue);
      
      return delta === 0 ? null : delta;
    },
    formatMonth(dateString) {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);
      return `${month} ${year}`;
    },
    formatDelta(delta) {
      if (delta === null || delta === undefined) {
        return '0';
      }
      return delta > 0 ? `+${delta}` : `${delta}`;
    },
    onPhoneNumberUpdate(payload) {
      this.phoneData = payload;
    },
    getCategoryDialOptions(category) {
      return {
        chart: {
          toolbar: { show: false },
          type: "radialBar"
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: "50%"
            },
            track: {
              background: "#e2e8f0",
              strokeWidth: "100%",
              margin: 5
            },
            dataLabels: {
              name: {
                show: false
              },
              value: {
                fontSize: "16px",
                fontWeight: 600,
                color: "#0f172a",
                formatter: () => `${Math.round(category.value)}%`,
                offsetY: 0,
                show: true
              },
              total: {
                show: false
              }
            }
          }
        },
        colors: ["#ff1493", "#1e3a8a", "#00d9ff"], // Hot pink, Deep blue, Light blue
        stroke: {
          lineCap: "round"
        }
      };
    },
    getCategoryColor(index) {
      const colors = [
        "#008ffb", // Blue
        "#00e396", // Green
        "#feb019", // Orange
        "#ff4560", // Red
        "#775dd0", // Purple
        "#00d9ff", // Cyan
        "#ff1493", // Hot Pink
        "#1e3a8a"  // Deep Blue
      ];
      return colors[index % colors.length];
    },
    async initializeData() {
      this.preloader = true;
      try {
        const currentProject = JSON.parse(localStorage.getItem("currentProject") || "{}");
        const userdata = JSON.parse(localStorage.getItem("userdata") || "{}");
        
        this.userdata = userdata;
        this.projectId = currentProject.id;
        this.projectid = this.projectId;
        this.project = currentProject.project_name;
        this.org = currentProject.company;
        this.city = currentProject.city;
        this.sharebyname = this.userdata.firstname+' '+this.userdata.lastname;
        // Get camera_id with fallbacks
        this.cameraId = await this.getCameraId();
        
        if (!this.cameraId) {
          console.error("Could not determine camera_id. Please select a camera.");
          this.preloader = false;
          return;
        }
        
        // Check if user has opticvyu access
        if (userdata.opticvyu_user && userdata.opticvyu_user === 1) {
          this.ov_access = true;
        } else {
          this.ov_access = false;
        }
        
        await Promise.all([
          this.fetchProgressData(),
          this.fetchCameraInfo(),
          this.loadLatestImage(),
          this.loadTimelapse(),
          this.nativagationImaegsThumnailList()
        ]);
      } catch (error) {
        console.error("Error initializing:", error);
      } finally {
        this.preloader = false;
      }
    },
    async fetchProgressData() {
      try {
        const filter = encodeURIComponent(
          JSON.stringify({
            where: {
              and: [
                { project_id: this.projectId },
                { camera_id: this.cameraId }
              ]
            }
          })
        );
        const url = `/longCompareRepports?filter=${filter}`;
        const response = await axios.get(url);
        const data = response.data;
        
        if (data && data.length > 0) {
          const latestReport = data[data.length - 1];
          this.latestReportId = latestReport.id;
          this.apiData = latestReport;
          this.processProgressData(latestReport);
        }
      } catch (error) {
        console.error("Error fetching progress data:", error);
      }
    },
    processProgressData(report) {
      const categories = report.categories || {};
      
      // Get latest date
      if (categories.overall && categories.overall.progress_history) {
        const history = categories.overall.progress_history;
        if (history.length > 0) {
          this.latestDate = history[history.length - 1].date;
        }
      }
      
      // Set overall progress
      if (categories.overall) {
        this.overallProgress = Math.round(categories.overall.current_completion || 0);
      }
      
      // Set multi-ring progress values (three rings: outermost, middle, innermost)
      // Using first three available categories or default values
      const ringCategories = [
        categories.structural_work,
        categories.external_facade,
        categories.architectural_works
      ].filter(Boolean);
      
      if (ringCategories.length >= 3) {
        this.multiRingProgress = [
          Math.round(ringCategories[0].current_completion || 0), // Outermost ring
          Math.round(ringCategories[1].current_completion || 0), // Middle ring
          Math.round(ringCategories[2].current_completion || 0)  // Innermost ring
        ];
      } else if (ringCategories.length > 0) {
        // Fill with available values and repeat if needed
        this.multiRingProgress = [
          Math.round(ringCategories[0]?.current_completion || 0),
          Math.round(ringCategories[1]?.current_completion || ringCategories[0]?.current_completion || 0),
          Math.round(ringCategories[2]?.current_completion || this.overallProgress || 0)
        ];
      } else {
        // Default to overall progress for all rings if no categories available
        this.multiRingProgress = [this.overallProgress, this.overallProgress, this.overallProgress];
      }
      
      // Set category dials
      const categoryMap = {
        structural_work: "Structural Work",
        external_facade: "External Façade",
        architectural_works: "Architectural Works",
        excavation: "Excavation",
        painting: "Painting"
      };
      
      this.categoryDials = Object.keys(categoryMap)
        .filter(key => categories[key])
        .map(key => ({
          key,
          label: categoryMap[key],
          value: Math.round(categories[key].current_completion || 0)
        }));
      
      // Set multi-ring progress for each category (3 rings per category)
      this.categoryMultiRings = {};
      this.categoryDials.forEach(category => {
        const categoryData = categories[category.key];
        const completion = Math.round(categoryData.current_completion || 0);
        
        // Create 3 rings with the category's completion value
        // You can modify this to use different values if you have sub-metrics
        this.categoryMultiRings[category.key] = [completion, completion, completion];
      });
      
      // Set all categories progress for single multi-ring chart
      // Sort by value (highest to lowest) for better visualization
      const sortedCategories = [...this.categoryDials].sort((a, b) => b.value - a.value);
      this.allCategoriesProgress = sortedCategories.map(cat => cat.value);
      this.allCategoriesLabels = sortedCategories.map(cat => ({
        label: cat.label,
        value: cat.value
      }));
      
      // Process line chart data
      this.processLineChartData(report);
    },
    processLineChartData(report) {
      const categories = report.categories || {};
      const predictions = report.predictions || {};
      
      // Get all unique dates from all categories
      const allDates = new Set();
      Object.keys(categories).forEach(key => {
        if (categories[key].progress_history) {
          categories[key].progress_history.forEach(item => {
            allDates.add(item.date);
          });
        }
      });
      
      // Add prediction dates
      if (predictions.one_month_ahead) {
        allDates.add(predictions.one_month_ahead.date);
      }
      if (predictions.two_months_ahead) {
        allDates.add(predictions.two_months_ahead.date);
      }
      
      const sortedDates = Array.from(allDates).sort();
      this.sortedDates = sortedDates; // Store for use in click handler
      
      // Create series for each category
      const categoryMap = {
        structural_work: "Structural Work",
        external_facade: "External Façade",
        architectural_works: "Architectural Works",
        excavation: "Excavation",
        painting: "Painting",
        overall: "Overall"
      };
      
      const colors = ["#008ffb", "#00e396", "#feb019", "#ff4560", "#775dd0", "#00d9ff"];
      let colorIndex = 0;
      
      this.lineChartSeries = Object.keys(categoryMap)
        .filter(key => categories[key])
        .map(key => {
          const category = categories[key];
          const data = sortedDates.map(date => {
            const historyItem = category.progress_history?.find(h => h.date === date);
            if (historyItem) {
              return historyItem.completion_percentage;
            }
            
            // Add predictions only for overall
            if (key === "overall") {
              if (predictions.one_month_ahead && date === predictions.one_month_ahead.date) {
                return predictions.one_month_ahead.expected_overall_completion;
              }
              if (predictions.two_months_ahead && date === predictions.two_months_ahead.date) {
                return predictions.two_months_ahead.expected_overall_completion;
              }
            }
            
            return null;
          });
          
          return {
            name: categoryMap[key],
            data: data,
            color: colors[colorIndex++ % colors.length],
            visible: true
          };
        });
      
      this.lineChartOptions = {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.onChartPointClick(event, chartContext, config);
            },
            markerClick: (event, chartContext, config) => {
              this.onChartPointClick(event, chartContext, config);
            }
          }
        },
        stroke: {
          curve: "smooth",
          width: 2
        },
        markers: {
          size: 5,
          hover: {
            size: 7
          },
          strokeWidth: 2,
          strokeColors: '#fff',
          fillOpacity: 1,
          shape: 'circle'
        },
        xaxis: {
          categories: sortedDates,
          labels: {
            rotate: -45,
            rotateAlways: true
          }
        },
        yaxis: {
          min: 0,
          max: 100,
          title: {
            text: "Completion %"
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ series, seriesIndex, dataPointIndex }) => {
            const category = this.lineChartSeries[seriesIndex];
            const date = sortedDates[dataPointIndex];
            const categories = this.apiData?.categories || {};
            
            // Find precise observation for this category and date
            let observation = "";
            const categoryKey = Object.keys(categoryMap).find(
              k => categoryMap[k] === category.name
            );
            
            if (categoryKey && categories[categoryKey]) {
              const historyItem = categories[categoryKey].progress_history?.find(
                h => h.date === date
              );
              if (historyItem) {
                observation = historyItem.precise_observation || "";
              }
            }
            
            return `
              <div class="custom-tooltip">
                <div><strong>${category.name}</strong></div>
                <div>Date: ${date}</div>
                <div>Progress: ${series[seriesIndex][dataPointIndex]}%</div>
                ${observation ? `<div class="observation">${observation}</div>` : ""}
              </div>
            `;
          }
        },
        legend: {
          show: false
        }
      };
    },
    async fetchCameraInfo() {
      try {
        const response = await axios.get(`/camera/${this.cameraId}`);
        if (response.data.success) {
          this.cameraS3Dir = response.data.success.s3dir || "";
          this.currentCameraTag = response.data.success.cam_name || "";
        }
      } catch (error) {
        console.error("Error fetching camera info:", error);
      }
    },
    async loadLatestImage() {
      try {
        const response = await axios.get(`/camera/${this.cameraId}/op_dashboard_latest_images`);
        if (response.data && response.data.url) {
          this.currentImage = response.data.url;
          this.loadImageList();
          this.checkFourite(this.cameraId);
        }
      } catch (error) {
        console.error("Error loading latest image:", error);
      }
    },
    async loadImageList() {
      try {
        const response = await axios.get(`/camera/${this.cameraId}/list/images`);
        if (response.data.success) {
          this.imageList = response.data.success;
          // Find current image index
          const currentImageId = this.currentImage.split("/").pop().split("?")[0];
          const index = this.imageList.findIndex(img => 
            img.image_name && img.image_name.includes(currentImageId)
          );
          if (index !== -1) {
            this.currentImageIndex = index;
          }
        }
      } catch (error) {
        console.error("Error loading image list:", error);
      }
    },
    async loadTimelapse() {
      try {
        // Default timelapse video for the project (camera 631)
        const response = await axios.get("/camera/" + this.cameraId + "/list/timelapse");
        const url = response.data?.success?.timelapse?.url;

        if (url) {
          this.timelapseUrl = url;
          this.checkcam360 = false; // regular MP4 timelapse

          // Ensure the browser reloads the video element when src changes
          this.$nextTick(() => {
            if (this.$refs.timelapseVideo) {
              this.$refs.timelapseVideo.load();
            }
          });
        } else {
          console.warn("Timelapse URL not found in response", response.data);
        }
      } catch (error) {
        console.error("Error loading timelapse:", error);
      }
    },
    onChartPointClick(event, chartContext, config) {
      if (!config || config.dataPointIndex === undefined || config.dataPointIndex === null) {
        return;
      }

      // Step 1: Get the clicked item's index value
      const clickedIndex = config.dataPointIndex;
      console.log("Clicked index:", clickedIndex);

      // Step 2: Get xaxis categories (dates array) - try multiple sources
      let categories = this.sortedDates || [];
      
      // Fallback: try to get from chart context
      if (categories.length === 0 && chartContext && chartContext.w && chartContext.w.globals) {
        categories = chartContext.w.globals.categoryLabels || [];
      }
      
      // Fallback: try to get from lineChartOptions
      if (categories.length === 0) {
        categories = this.lineChartOptions?.xaxis?.categories || [];
      }
      
      console.log("Categories (dates array):", categories);
      console.log("Categories length:", categories.length);

      // Step 3: Get the date that belongs to the clicked index
      const date = categories[clickedIndex];
      console.log("Date for index", clickedIndex, ":", date);

      if (!date) {
        console.warn("No date found for index", clickedIndex, "Categories:", categories);
        return;
      }

      // Step 4: Get image according to the date
      this.loadImageForDate(date);
    },
    async loadImageForDate(date) {
      this.preloader = true;
      try {
        // date is in format YYYY-MM-DD -> convert to YYYY_MM_DD (with underscores)
        const parts = date.split("-");
        if (parts.length !== 3) {
          console.warn("Unexpected date format for image load:", date);
          return;
        }
        const dateFormatted = `${parts[0]}_${parts[1]}_${parts[2]}`;

        // Use the new API endpoint
        const response = await axios.get(
          `https://api.opticvyu.com/api/project/get-thumbnail-image-by-date`,
          {
            params: {
              camera_id: this.cameraId,
              project_id: this.projectId,
              date: dateFormatted
            }
          }
        );

        if (response.data && response.data.success.url) {
          this.currentImage = response.data.success.url;
          this.preloader = false;
        } else {
          console.warn("No image URL found in response for date", date);
          this.preloader = false;
        }
      } catch (error) {
        console.error("Error loading image for date:", error);
      }
    },
    toggleSeries(index) {
      this.lineChartSeries[index].visible = !this.lineChartSeries[index].visible;
      // Update chart visibility
      this.$refs.lineChart?.updateSeries([...this.lineChartSeries]);
    },
    previousImage() {
      if (this.canGoPrevious) {
        this.currentImageIndex--;
        this.loadImageById(this.imageList[this.currentImageIndex].id);
      }
    },
    nextImage() {
      if (this.canGoNext) {
        this.currentImageIndex++;
        this.loadImageById(this.imageList[this.currentImageIndex].id);
      }
    },
    async loadImageById(imageId) {
      try {
        const response = await axios.get(`/camera/${this.cameraId}/image/${imageId}/list/url`);
        if (response.data.success) {
          this.currentImage = response.data.success.image_url;
        }
      } catch (error) {
        console.error("Error loading image:", error);
      }
    },
    openFullscreenImage() {
      this.fullscreenImageUrl = this.currentImage;
      this.fullscreenImageDialog = true;
    },
    openEditDialog(category) {
      this.editCategory = category;
      this.editProgressValue = this.overallProgress;
      this.editDialog = true;
    },
    openManualUpdateDialog() {
      this.manualUpdateInstructions = "";
      this.manualUpdateDialog = true;
    },
    closeManualUpdateDialog() {
      this.manualUpdateDialog = false;
      this.manualUpdateInstructions = "";
    },
    async submitManualUpdate() {
      if (!this.manualUpdateInstructions || this.manualUpdateInstructions.trim() === '') {
        return;
      }

      this.manualUpdateLoading = true;
      this.preloader = true;

      try {
        // Get existing JSON data
        if (!this.apiData) {
          throw new Error("No API data available");
        }

        // Get all dates from overall category progress_history
        const overallCategory = this.apiData.categories?.overall;
        if (!overallCategory || !overallCategory.progress_history) {
          throw new Error("No progress history found");
        }

        const dates = overallCategory.progress_history.map(item => item.date);
        
        // Get image URLs for all dates
        const imageURLs = [];
        for (const date of dates) {
          try {
            const parts = date.split("-");
            const dateFormatted = `${parts[0]}_${parts[1]}_${parts[2]}`;
            
            const response = await axios.get(
              `https://api.opticvyu.com/api/project/get-thumbnail-image-by-date`,
              {
                params: {
                  camera_id: this.cameraId,
                  project_id: this.projectId,
                  date: dateFormatted
                }
              }
            );

            if (response.data && response.data.success && response.data.success.url) {
              imageURLs.push(response.data.success.url);
            }
          } catch (error) {
            console.warn(`Error fetching image for date ${date}:`, error);
          }
        }

        if (imageURLs.length === 0) {
          throw new Error("No image URLs found");
        }

        // Prepare prompt with existing JSON and user instructions
        const existingJsonString = JSON.stringify(this.apiData);
        const prompt = `${existingJsonString}\n\nUser Instructions: ${this.manualUpdateInstructions}`;

        // Prepare instruction text
       // const instruction = `You are an expert Construction Progress Analyst. Your task is to analyze the images provided along with the added instructions and update the input JSON data accordingly. STRICT RULES: 1. Above all, keep the user instructions to reprocess the completion_percentage and precise_observation. Assume that user instructions are perfect and output should strictly follow the instructions provided without any deviation. 2. Maintain the EXACT SAME JSON structure, keys, fields, and nesting as the input JSON. 3. Only update values that can be validated from the images. 4. Write precise_observation based on visible evidence only. Do not guess. 5. Progress percentage values must be numeric and reflect the visible progress in the images. 6. Do not add new JSON fields, rename fields, or remove fields. 7. Do not change the overall schema or formatting. 8. If the updated value is the same as previous, keep it unchanged. OUTPUT REQUIREMENTS: Return ONLY the updated JSON as output. Output must match the exact same structure as the input JSON. No explanations, no introduction text, no markdown, no extra commentary.`;

        // Prepare form data
        const formData = new URLSearchParams();
        formData.append('prompt', prompt);
        formData.append('camera_id', this.cameraId);
        formData.append('project_id', this.projectId);
        // Add all image URLs as comma-separated string
        formData.append('imageURLs', imageURLs.join(','));

       
        // Call NLP API
        const nlpResponse = await axios.post(
          'https://opticvyu.com/api/camera/update-progress-long-turm-analze',
          formData,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Accept': 'application/json'
            }
          }
        );
        console.log("nlpResponse", nlpResponse);

        // Check if response is successful
        if (nlpResponse.data && (nlpResponse.data.success || nlpResponse.status === 200)) {
          // Close dialog and show success message
          this.closeManualUpdateDialog();
          this.$swal({
            icon: "success",
            title: "Success",
            text: "Progress has been updated successfully!"
          }).then(() => {
            // Reload the page after success
            window.location.reload();
          });
        } else {
          throw new Error("API response indicates failure");
        }
      } catch (error) {
        console.error("Error in manual update:", error);
        // Show error notification
        this.$swal({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || error.message || "Something went wrong. Please try again."
        });
      } finally {
        this.manualUpdateLoading = false;
        this.preloader = false;
      }
    },
    async saveProgress() {
      try {
        if (!this.apiData || !this.apiData.categories || !this.apiData.categories.overall) {
          console.error("API data not loaded correctly, cannot save progress.");
          return;
        }

        // Preserve all categories and only update 'overall'
        const existingCategories = this.apiData.categories || {};
        const existingOverall = existingCategories.overall || {};
        const existingHistory = existingOverall.progress_history || [];

        let newHistory;
        if (
          existingHistory.length > 0 &&
          existingHistory[existingHistory.length - 1].date === this.latestDate
        ) {
          // Update the latest entry's completion_percentage
          newHistory = existingHistory.map((item, index) => {
            if (index === existingHistory.length - 1) {
              return {
                ...item,
                completion_percentage: this.editProgressValue
              };
            }
            return item;
          });
        } else {
          // Append a new history entry for the latest date
          newHistory = [
            ...existingHistory,
            {
              date: this.latestDate,
              completion_percentage: this.editProgressValue,
              precise_observation: "Manually updated"
            }
          ];
        }

        const updatedCategories = {
          ...existingCategories,
          overall: {
            ...existingOverall,
            progress_history: newHistory,
            current_completion: this.editProgressValue
          }
        };

        const updateData = {
          categories: updatedCategories
        };
        
        await axios.patch(
          `/longCompareRepports/${this.latestReportId}`,
          updateData
        );
        
        // Update local state to reflect changes from PATCH
        this.apiData = {
          ...this.apiData,
          categories: updatedCategories
        };
        this.processProgressData(this.apiData);

        this.overallProgress = this.editProgressValue;
        this.editDialog = false;
        this.$swal({
          icon: "success",
          title: "Progress Updated",
          text: "Overall progress has been updated successfully."
        });
      } catch (error) {
        console.error("Error saving progress:", error);
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Failed to update progress. Please try again."
        });
      }
    },
    goToDashboard() {
      this.$router.push("/dashboard");
    },
    goToProjectSettings() {
      this.$router.push("/dashboard/settings/profile");
    },
    shareTimelapse(value) {
      // Implement share functionality similar to timelapse page
      console.log("Share timelapse via", value);

      if (value == 'email') {
                this.sharebox = true;
            }
            if (value == 'whatsapp' ) {
                 this.whatsapp_sharebox = true;
            }
    },
    onTimelapseLoad() {
      // Handle timelapse load
    },
    async refreshImages() {
      this.preloader = true;
      await Promise.all([
        this.loadLatestImage(),
        this.nativagationImaegsThumnailList()
      ]);
      this.refresh++;
      this.refreshtwoo++;
      this.preloader = false;
    },
    regulartimelineshow() {
      this.regulertimeline = !this.regulertimeline;
    },
    async nativagationImaegsThumnailList() {
      try {
        // Get daywise thumbnail images for regular timeline
        const response = await axios.get(`/camera/${this.cameraId}/get_thumnail_images`);
        if (response.data && response.data.success) {
          const daywiseimageidsarray = response.data.success;
          let i = 0;
          daywiseimageidsarray.forEach((item) => {
            const date = item.image_name.split("_");
            const d = date[2] + "-" + date[1] + "-" + date[0];
            daywiseimageidsarray[i].display_date = d;
            i++;
          });
          this.daywiseimageidsarray = daywiseimageidsarray.reverse();
          this.refresh++;
        }
        
        // Get timeslots for archive timeline
        const timeslotsResponse = await axios.get(`/camera/${this.cameraId}/list/images`);
        if (timeslotsResponse.data && timeslotsResponse.data.success) {
          this.timeslots = timeslotsResponse.data.success;
          this.refreshtwoo++;
        }
      } catch (error) {
        console.error("Error loading thumbnail list:", error);
      }
    },
    selectThumbnail(prefix, imageName) {
      // Load image when thumbnail is clicked
      this.loadImageByPrefix(prefix, imageName);
    },
    selectThumainal(prefix, imagename) {
      // Load image when thumbnail is clicked (Home.vue style)
      this.getSignUrlBykey(prefix, imagename);
    },
    async loadImageByPrefix(prefix, imageName) {
      try {
         console.log(
          "prefix", prefix,
          "imageName", imageName
         )
        // Load image using prefix and image name
        // Implementation similar to Home.vue
      } catch (error) {
        console.error("Error loading image:", error);
      }
    },
    async getSignUrlBykey(prefix, name) {
      try {
        this.preloader = true;
        this.currentImage = "";
        const currentProject = JSON.parse(localStorage.getItem("currentProject") || "{}");
        const projectId = currentProject.id || this.projectId;
        
        const response = await axios.get(
          `/get_sign_url_by_key?key=${prefix}/${name}.jpg&exp=5minutes&project_id=${projectId}`
        );
        
        if (response.data && response.data.success && response.data.success.url) {
          this.currentImage = response.data.success.url;
          this.loadImageList();
        }
        this.preloader = false;
      } catch (error) {
        console.error("Error getting sign URL:", error);
        this.preloader = false;
      }
    },
    reloade() {
      // Reload the page
      this.$router.go();
    },
    delete_img_archive() {
      if (this.selected_archive.length > 0) {
        console.log('Deleting images:', this.selected_archive);
        this.preloader = true;
        
        const currentProject = JSON.parse(localStorage.getItem("currentProject") || "{}");
        const userdata = JSON.parse(localStorage.getItem("userdata") || "{}");
        
        axios({
          url: `/camera/${this.cameraId}/image/list_delete_req`,
          method: 'post',
          data: {
            projectname: currentProject.project_name || "",
            project_id: this.projectId || currentProject.id || "",
            camid: this.cameraId,
            cam_dir: this.cameraS3Dir || "",
            userid: userdata.id || "",
            usermail: userdata.email || "",
            img_list: this.selected_archive
          }
        })
        .then((resp) => {
          console.log("Delete response", resp);
          this.preloader = false;
          
          if (resp.data.success) {
            this.$swal({
              icon: "success",
              title: "Success",
              text: resp.data.success,
              showConfirmButton: true,
            });
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          } else {
            this.$swal({
              icon: "error",
              title: "Error",
              text: resp.data.error || "Failed to delete images",
              showConfirmButton: true,
            });
          }
        })
        .catch((err) => {
          this.preloader = false;
          console.error("Error deleting images:", err);
          this.$swal({
            icon: "error",
            title: "Error",
            text: "Failed to delete images. Please try again.",
            showConfirmButton: true,
          });
        });
      } else {
        this.preloader = false;
        this.$swal({
          icon: "warning",
          title: "No Selection",
          text: "Image is not selected. Please select the image then click on the delete image option",
          showConfirmButton: true,
        });
      }
    },
    OnSelect() {
      if (this.selected_archive.length > 0) {
        this.show_deletebtn = true;
      } else {
        this.show_deletebtn = false;
      }
    },
    getArchiveImageId(imageId) {
      // Load archive image
      this.loadImageById(imageId);
    },
    shareWhatsappImage() {
      this.send = true;
            
            axios({
                    url: "https://secure.opticvyu.com/api/send-to-whatsapp-with-attachment",
                    data: this.whatsapp_params_image,
                    method: "post",
                })
                .then((resp) => {
                    console.log(resp);
                    this.whatsapp_sharebox_image = false;
                    this.send = false;
                    this.snackbar = true;
                    this.text = `Successfully sent.`;
                })
                .catch((err) => {
                    console.log(err);
                    this.send = false;
                    this.snackbar = true;
                    this.text = `Delivery faild.`;
                    this.preloader = false;
                });
    },
    shareWhatsapp() {
        this.send = true;
        axios({
          url: "https://secure.opticvyu.com/api/send-to-whatsapp-with-link",
          data: {
              whatsapp_number: this.phoneData.formattedNumber,
              attachment: this.timelapseUrl,
              project: this.project,
              org: this.org,
              city: this.city,
            },
          method: "post",
        })
          .then((resp) => {
            console.log(resp);
            this.shareWhatsapp = false;
            this.send = false;
            this.snackbar = true;
            this.text = `Successfully sent.`;
          })
          .catch((err) => {
            console.log(err);
            this.send = false;
            this.snackbar = true;
            this.text = `Delivery faild.`;
            this.preloader = false;
          });
    },
    async submit() {
      const valid = await this.$refs.form.validate();
      if (valid) {
        this.sharetimelaps(this.paramstimelapse); // action to login
      }
    },
    sharetimelaps(sharedata) {
      console.log("data", sharedata);
      this.send = true;

      axios({
        url: "https://secure.opticvyu.com/api/send-with-link",
        data: sharedata,
        method: "post",
      })
        .then((resp) => {
          console.log(resp);
          this.sharebox = false;
          this.send = false;
        })
        .catch((err) => {
          console.log(err);
          this.send = false;
          this.snackbar = true;
          this.text = "Email delivery faild";
        });
    },
    copyTestingCode() {
      let testingCodeToCopy = document.querySelector("#testing-code");
      testingCodeToCopy.setAttribute("type", "text");
      testingCodeToCopy.select();

      try {
        var successful = document.execCommand("copy");
        var msg = successful ? "URL copied" : "URL not copied";
        //alert("timelapse url was copied " + msg);
        this.snackbar = true;
        this.text = msg;
      } catch (err) {
        // alert("Oops, unable to copy");
        this.snackbar = true;
        this.text = "some thing went wrong";
      }

      /* unselect the range */
      testingCodeToCopy.setAttribute("type", "hidden");
      window.getSelection().removeAllRanges();
    },
    // Image menu methods
    liveimageRequest() {
      if (!this.current_cam || !this.current_cam.camera_id) {
        console.warn("Camera not available");
        return;
      }
      axios
        .get("/camera/" + this.current_cam.camera_id + "/live_image")
        .then((resp) => {
          console.log("Live image request response", resp);
          this.preloader = true;
          this.getliveimage();
        })
        .catch((err) => {
          console.log("Live image error", err);
          this.preloader = false;
        });
    },
    getliveimage() {
      if (!this.current_cam || !this.current_cam.camera_id) {
        console.warn("Camera not available");
        return;
      }
      axios
        .get("/camera/" + this.current_cam.camera_id + "/get_live_image")
        .then((resp) => {
          if (resp.data && resp.data.success) {
            this.currentImage = resp.data.success.image_url;
            this.preloader = false;
          } else {
            this.preloader = false;
            this.snackbar = true;
            this.text = "Live image not found";
          }
        })
        .catch((err) => {
          console.log("Get live image error", err);
          this.preloader = false;
        });
    },
    closeAnnotate() {
      this.activeanotate = false;
      this.showannotation = false;
    },
    getAnnotationData() {
      // Implementation for getting annotation data
      console.log("Get annotation data");
      this.activeanotate = false;
      this.showannotation = false;
    },
    initAnno() {
      this.activeanotate = true;
      this.showannotation = true;
    },
    annotadeImage() {
            this.preloader = true;
            axios({
                    url: "/image/baseimage",
                    method: "GET",
                    params: {
                        "image": this.currentImage
                    }
                })
                .then((resp) => {

                    this.showannotation = true;
                    var data = resp.data.success;
                    this.preloader = false;
                    //console.log(resp.data.success);
                    this.options.includeUI.loadImage.path = data;
                
                })
        },
    boxopenshareEmails(value) {
      if (value === 'email') {
        this.sharebox = true;
      }
      if (value === 'whatsapp') {
        this.whatsapp_sharebox = true;
      }
    },
    boxopenshareImageByEmails(value) {
      if (value === 'email') {
        this.shareboxImage = true;
      }
      if (value === 'whatsapp') {
        this.whatsapp_sharebox_image = true;
      }
    },
    evnettaginboxopener() {
      this.evnettaginbox = true;
    },
    savefavoourite() {
           
            var res = this.currentImage.split("?");

            var namestring = res[0];

            var a = namestring.split("/");

            //  console.log(a[a.length-1]);

            var b = a[a.length - 1];

            var c = b.split(".");

            // console.log(c[0]);
            var eventd;
            if (this.isFav) {
                eventd = {
                    key: c[0],
                };
            } else {
                eventd = {
                    key: c[0],
                    add: 1,
                };
            }

            axios({
                    url: "/project/" +
                        this.projectid +
                        "/camera/" +
                        this.cameraId +
                        "/favourite",
                    params: eventd,
                    method: "get",
                })
                .then((resp) => {
                    console.log(resp);
                    this.snackbar = true;

                    if (this.isFav) {
                        this.text = "Removed Favorite";
                        this.favourite = "mdi-star";
                    } else {
                        this.text = "Added Favorite";

                        this.favourite = "mdi-star-outline";
                    }
                    this.checkFourite(this.cameraId);
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        checkFourite(id) {
            //  this.send = true;

            console.log(this.currentImage);

            var res = this.currentImage.split("?");

            var namestring = res[0];

            var a = namestring.split("/");

            //  console.log(a[a.length-1]);

            var b = a[a.length - 1];

            var c = b.split(".");

            //  console.log(c[0]);

            var eventd = {
                key: c[0],
            };

            axios({
                    url: "/project/" + this.projectid + "/camera/" + id + "/check_favourite",
                    params: eventd,
                    method: "get",
                })
                .then((resp) => {
                   // console.log("favourite", resp);

                    if (resp.data.success.data) {
                        this.favourite = "mdi-star";
                        this.isFav = true;
                    } else {
                        this.favourite = "mdi-star-outline";
                        this.isFav = false;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        },
    downloadImage() {
      // Open image in new tab for download
      window.open(this.currentImage, '_blank');
    },
    fullscreen() {
      this.openFullscreenImage();
    },
    getSelectedDate(date) {
      // Load images for selected date
      this.loadImagesForDate(date);
    },
    async loadImagesForDate(date) {
      try {
        const parts = date.split("-");
        const nd = `${parts[0]}${parts[1]}${parts[2]}`;
        const response = await axios.get(`/camera/${this.cameraId}/list/images/date/${nd}`);
        if (response.data && response.data.success) {
          this.timeslots = response.data.success;
          this.timeslotloader = false;
        }
      } catch (error) {
        console.error("Error loading images for date:", error);
        this.timeslotloader = false;
      }
    },
    selectTimeSlot(imageId) {
      this.loadImageById(imageId);
      this.dialog = false;
    },
    dialogClose() {
      this.dialog = false;
    },
    datesWithImage() {
      // Get dates with images
      // TODO: Implement dates with images functionality
      console.log("Dates with images");
    },
    SaveandSend() {
            //imgEl.src
          const myImageb = this.$refs.tuiImageEditor.invoke("toDataURL");
            
          // console.log("Edit data", myImageb);
             
           const _scale = 0.5;
    var img = document.createElement('img');
    img.setAttribute("src", myImageb);

     img.onload = () => {
         var canvas = document.createElement('canvas');
         canvas.width = img.width * _scale;
        canvas.height = img.height * _scale;

         var ctx = canvas.getContext("2d");
         //var cw = canvas.width;
        // var ch = canvas.height;
         var maxW = img.width * _scale;
         var maxH = img.height * _scale;

        var iw = img.width;
         var ih = img.height;
         var scl = Math.min((maxW / iw), (maxH / ih));
         var iwScaled = iw * scl;
         var ihScaled = ih * scl;
         canvas.width = iwScaled;
         canvas.height = ihScaled;
         ctx.drawImage(img, 0, 0, iwScaled, ihScaled);
        const newBase64 = canvas.toDataURL("image/jpeg", scl);
 
           //return newBase64;
           
            var a = document.createElement("a"); //Create <a>
            a.href = newBase64; //Image Base64 Goes here
           a.download = this.filename; //File name Here
           a.click(); //Downloaded file
           this.annotate_newbase64 = newBase64;
           this.Save_annotate_img(newBase64);
           this.showannotation = false;
          //callBack(newBase64);
      }

         },
         Save_annotate_img(newBase64img) {
              if (this.annotate_newbase64){
                 this.preloader = true;
                    console.log("testing variable", newBase64img);
                    this.imgname = "markup_"+this.filename;
                  // console.log(url1);
            axios({
                    url: '/camera/' +
                    this.current_cam.camera_id +'/save_dash_annotate_img',
                    method: 'post',
                    data: {
                        proj_dir: this.proj_dir,
                        projectname: this.project,
                        project_id: this.projectid,
                        userid: this.userdata.id,
                        usermail:this.userdata.email,
                        imgbs64:this.annotate_newbase64,
                        imgname: this.imgname,
                        camid: this.current_cam.camera_id,
                        cam_dir: this.lcamprefix,
                    }
                })
                .then((resp) => {
                     console.log("image added to cart", resp.data);
                    if (resp.data.success){
                       this.preloader = false;
                       // this.commmentForm = false;
                   this.snackbar = true;
                  this.text = `Annotated image successfully downloaded & ready to share.`;
                  this.annotateurl = resp.data.imgurl;
                  this.annotate_data = resp.data.imgdata;
                  this.boxopenshareEmails_annotate();
                    /*   setTimeout(()=>{
                       // this.getCartimg();
                       this.$router.go();
                    }, 4000); */
                    }else{
                      this.preloader = false;
                      this.snackbar = true;
                      this.text = resp.data.error;
                      setTimeout(()=>{
                        this.$router.go();
                      }, 4000);
                    }
                })
                .catch(err => {
                    console.log(err);
                    this.alert = true;
                    this.alertmessage = "Something is going wrong. Please try again.";
                    this.alertType = "error"
                    this.preloader = false
                })
        
              }
        },
        boxopenshareEmails_annotate() {
            this.sharebox_annotate = true;
            // this.params.attachment = this.source[0].url;
        },
        async submit_annotate() {
            const valid = await this.$refs.form.validate();
            if (valid) {
                this.shareEmail_annotate(this.params_annotate); // action to login
            }
        },
        shareEmail_annotate(sharedata_annotate) {
            //  console.log("data", sharedata);
            this.send = true;

            axios({
                    url: "https://secure.opticvyu.com/api/send-with-attachment_annotate",
                    data: sharedata_annotate,
                    method: "post",
                })
                .then((resp) => {
                    console.log(resp);
                    this.sharebox_annotate = false;
                    this.send = false;
                    this.snackbar = true;
                    this.text = `Image successfully sent.`;
                })
                .catch((err) => {
                    console.log(err);
                    this.send = false;
                    this.snackbar = true;
                    this.text = `Email delivery faild.`;
                    this.preloader = false;
                });
        },
        wathershow() {
            axios({
                    url: "/weather/project/" + this.projectid,
                    method: "get"
                })
                .then((resp) => {
                     console.log(resp);
                    this.watherdata = resp.data;
                    this.watherview = !this.watherview;
                })
                .catch((err) => {
                    console.log(err);
                    this.watherview = !this.watherview;
                });
        },
        showannotationclose() {
            this.tui_re_render++;
            this.showannotation = false;
            self.$refs.tuiImageEditor.invoke("clearObjects");
        },
        async submitevent() {
            const valid = await this.$refs.form.validate();
            if (valid) {
                this.eventTaginform(); // action to login
            }
        },
        eventTaginform() {
            //  this.send = true;

            // console.log(this.image);

            var res = this.currentImage.split("?");

            var namestring = res[0];

            var a = namestring.split("/");

            //  console.log(a[a.length-1]);

            var b = a[a.length - 1];

            var c = b.split(".");

            // console.log(c[0]);

            var eventd = {
                img: c[0],
                description: this.eventdata,
            };

            axios({
                    url: "/project/" +
                        this.projectid +
                        "/camera/" +
                        this.cameraId +
                        "/event-tag",
                    params: eventd,
                    method: "get",
                })
                .then((resp) => {
                    //console.log(resp);
                    this.sharebox = false;
                    this.send = false;
                    this.snackbar = true;
                    this.text = resp.data.msg;
                    this.evnettaginbox = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.send = false;
                });
        },
        setrouter: function (url) {
            if (this.$route.path !== url) {
                this.$router.push(url);
                //console.log("current router ", this.$route.path);
                this.activelink = this.$route.path;
            }

            //console.log(url);
        },
        async submit2() {
            const valid = await this.$refs.form2.validate();
            if (valid) {
                this.shareEmailImage(this.params); // action to login
            }
        },
        shareEmailImage(sharedata) {
              console.log("data", sharedata);
            this.send = true;

            axios({
                    url: "https://secure.opticvyu.com/api/send-with-attachment",
                    data: sharedata,
                    method: "post",
                })
                .then((resp) => {
                    console.log(resp);
                    this.sharebox = false;
                    this.send = false;
                    this.snackbar = true;
                    this.text = `Successfully sent.`;
                })
                .catch((err) => {
                    console.log(err);
                    this.send = false;
                    this.snackbar = true;
                    this.text = `Delivery faild.`;
                    this.preloader = false;
                });
        },
  }
};
</script>

<style lang="scss" scoped>
.menusioc {
  border: 2px solid #fff !important;
  box-shadow: 0 0 12px 1px rgba(255, 255, 255, 0.8);
  
  .v-icon {
    filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.5));
  }
}
.dashboard-ai {
  height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.page-header {
  margin-bottom: 10px;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
  }
  
  .page-title {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }
  
  .latest-observation {
    font-style: italic;
    color: #666;
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
  }
  
  .edit-project-btn {
    text-transform: none;
  }
}

.blocks-grid {
  margin: 0;
  flex: 1 1 auto;
  
  .v-col {
    display: flex;
    flex-direction: column;
    padding: 10px !important;
    box-sizing: border-box;
  }
}

.block-container {
  padding: 0;
  display: flex;
  width: 100%;
  flex: 1 1 auto;
  box-sizing: border-box;
}

.block-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  height: 100%;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-height: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  
  .block-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 0px;
    color: #333;
    flex-shrink: 0;
  }
}

.progress-dials-block {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  .dials-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 24px;
    flex: 1 1 auto;
    min-height: 0;
    
    .single-chart-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 30px;
      width: 100%;
      height: 100%;
      
      .chart-legend-custom {
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-width: 200px;
        
        .legend-item-custom {
          display: flex;
          align-items: center;
          gap: 10px;
          
          .legend-color {
            width: 16px;
            height: 16px;
            border-radius: 3px;
            flex-shrink: 0;
          }
          
          .legend-text {
            font-size: 14px;
            font-weight: 500;
            color: #333;
          }
        }
      }
    }
    
    .overall-dial {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      
      .dial-actions {
        position: absolute;
        top: 10px;
        right: 10px;
        display: flex;
        gap: 8px;
        z-index: 10;
      }
      
      .dial-edit-icon,
      .dial-manual-update-icon {
        cursor: pointer;
        background: rgba(255,255,255,0.9);
        border-radius: 50%;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        
        &:hover {
          background: rgba(255,255,255,1);
        }
      }
    }
    
    .small-dials-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
      width: 55%;
      align-content: center;
    }
    
    .dial-label {
      text-align: center;
      margin-top: 10px;
      font-weight: 500;
      font-size: 14px;
    }
  }
}

.chart-block {
  .chart-container {
    position: relative;
    height: 100%;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
    
    .chart-legend {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      margin-top: 15px;
      padding-top: 15px;
      border-top: 1px solid #eee;
      flex-shrink: 0;
      
      .legend-item {
        display: flex;
        align-items: center;
        gap: 1px;
        cursor: pointer;
        padding: 5px 1px;
        border-radius: 4px;
        transition: background 0.2s;
        
        &:hover {
          background: #f5f5f5;
        }
        
        &.disabled {
          opacity: 0.3;
        }
        
        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 3px;
        }
        
        .legend-label {
          font-size: 12px;
        }
      }
    }
  }
}

.timelapse-block {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  .timelapse-container {
    position: relative;
    height: 100%;
    flex: 1 1 auto;
    min-height: 0;
    
    .timelapse-video {
      width: 100%;
      height: 100%;
      border-radius: 4px;
      object-fit: cover;
    }
    
    .share-dial {
      position: absolute;
      top: 50%;
      left: 20px;
    }
  }
}

.dashboard-mini-block {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  .mini-dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0px;
    flex-shrink: 0;
  }
  
  .mini-image-container {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    
    .image_menus {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 10;
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: flex-end;
    }
    
    .mini-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      cursor: pointer;
      border-radius: 4px;
    }
    
    .image-navigation {
      position: absolute;
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 10px;
      background: rgba(0,0,0,0.5);
      padding: 5px;
      border-radius: 20px;
    }
  }
}

.timeList {
  max-height: 341px;
  overflow-y: auto;
  
  .itemBottomMargin {
    margin-bottom: 8px;
  }
}

.bottom-menu {
  position: fixed;
  bottom: 16px;
  background: white;
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  z-index: 1000;
  min-width: 50%;
  
  .timeline {
    align-items: center;
    gap: 12px;
    width: 95%;
    
    .timeline-controls {
      gap: 12px;
    }
    
    &:not(.active) {
      .timelinec {
        display: none !important;
      }
    }
    
    &.active {
      .timelinec {
        display: block;
      }
    }
    
    .listitem {
     
      align-items: center;
      gap: 8px;
      list-style: none;
      padding: 0;
      margin: 0;
      
      li {
      
        align-items: center;
      }
    }
    
    .timelinetoggle {
      cursor: pointer;
      
      align-items: center;
    }
    
    .timelinec {
      margin-top: 12px;
      width: 100%;
      max-width: 100%;
      
      .fixImageBox {
        display: block;
        height: 96px;
        overflow: hidden;
        border-radius: 4px;
        cursor: pointer;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
      
      .disply_date {
        font-size: 12px;
        text-align: center;
        margin-top: 4px;
        color: #666;
      }
      
      .checkboxxsle {
        position: absolute;
        top: 5px;
        left: 5px;
        z-index: 10;
        width: 20px;
        height: 20px;
      }
    }
  }
}

.limitations-btn {
  margin: 20px;
}

.observations-table-container {
  margin-top: 20px;
  margin-bottom: 20px;
  
  .observations-card {
    .block-title {
      margin-bottom: 20px;
    }
    
    .v-data-table {
      background: white;
    }
    
    .category-name {
      font-weight: 600;
      color: #333;
      min-width: 150px;
    }
    
    .completion-percent {
      font-weight: 600;
      color: #0f172a;
      min-width: 120px;
    }
    
    .observation-text {
      color: #666;
      line-height: 1.6;
    }
    
    thead th {
      background-color: #f5f5f5;
      font-weight: 600;
      color: #333;
      border-bottom: 2px solid #e0e0e0;
    }
    
    tbody tr {
      border-bottom: 1px solid #e0e0e0;
      
      &:hover {
        background-color: #f9f9f9;
      }
    }
    
    tbody td {
      padding: 16px;
      vertical-align: top;
    }
  }
}

.execution-velocity-container {
  margin-top: 20px;
  margin-bottom: 100px; // Space for bottom menu
  
  .velocity-card {
    .block-title {
      margin-bottom: 20px;
    }
    
    .month-name {
      font-weight: 600;
      color: #333;
      min-width: 120px;
    }
    
    .delta-value {
      font-weight: 500;
      color: #0f172a;
      min-width: 100px;
    }
    
    thead th {
      background-color: #f5f5f5;
      font-weight: 600;
      color: #333;
      border-bottom: 2px solid #e0e0e0;
    }
    
    tbody tr {
      border-bottom: 1px solid #e0e0e0;
      
      &:hover {
        background-color: #f9f9f9;
      }
    }
    
    tbody td {
      padding: 12px 16px;
      vertical-align: middle;
    }
  }
}

.fullscreen-image-card {
  .fullscreen-header {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 100;
    background: rgba(0,0,0,0.5);
  }
  
  .fullscreen-image-container {
    position: relative;
    width: 100%;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .fullscreen-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
    
    .fullscreen-options {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
    }
    
    .camera-tag {
      position: absolute;
      bottom: 20px;
      left: 20px;
      background: rgba(0,0,0,0.7);
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
    }
  }
}

.limitations-list {
  padding-left: 20px;
  
  li {
    margin-bottom: 15px;
    line-height: 1.6;
  }
}

@media (max-width: 960px) {
  .blocks-grid {
    .block-container {
      margin-bottom: 20px;
    }
  }
  
  .small-dials-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}
</style>

